import { apiClient } from "@/lib/api/http"

export interface CreateQuotationSellerToAdminPayload {
  rfqId: string
  adminToSellerRfqId: string
  companyId: string
  message: string
  currencyCode: string
  originCityId?: string
  commodity?: string
  weight?: number
  volumeWeight?: number
  totalPieces?: number
  expiresAt?: string
  charges: {
    description: string
    unitPrice: number
    discount: number
    quantity: number | null
    measureUnit?: string
    tax: number
    taxType: string
  }[]
}

export interface CreateQuotationAdminToBuyerPayload {
  rfqId: string
  adminToSellerRfqId: string
  companyId: string
  message: string
  currencyCode: string
  expiresAt?: string
  charges: {
    description: string
    unitPrice: number
    discount: number
    quantity: number | null
    measureUnit?: string
    tax: number
    taxType: string
  }[]
  sellerToAdminQuotationId?: string
}

interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

export async function createQuotationSellerToAdmin(
  payload: CreateQuotationSellerToAdminPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    "/quotation/seller-to-admin",
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export interface QuotationFilterParams {
  status?: string
  companyId?: string
  buyerId?: string
  fromDate?: string
  toDate?: string
  isLatest?: boolean
  page?: number
  limit?: number
}

export async function getSellerQuotations(token: string, filters?: QuotationFilterParams, signal?: AbortSignal): Promise<any> {
  const { data: envelope } = await apiClient.get<any>(
    "/quotation/seller-to-admin",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: filters,
      signal,
    }
  )
  
  const quotations = envelope?.data || []
  const totalCount = envelope?.meta?.total ?? quotations.length
  const totalPages = envelope?.meta?.totalPages ?? Math.ceil(totalCount / (filters?.limit || 10))

  Object.defineProperty(quotations, "meta", {
    value: envelope?.meta,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(quotations, "totalCount", {
    value: totalCount,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(quotations, "totalPages", {
    value: totalPages,
    enumerable: false,
    writable: true,
    configurable: true
  })

  return quotations
}

export async function sendQuotationAdminToBuyer(
  payload: CreateQuotationAdminToBuyerPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    "/quotation/admin-to-buyer",
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getAdminToBuyerQuotations(token: string, filters?: QuotationFilterParams, signal?: AbortSignal): Promise<any> {
  const { data: envelope } = await apiClient.get<any>(
    "/quotation/admin-to-buyer",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: filters,
      signal,
    }
  )
  
  const quotations = envelope?.data || []
  const totalCount = envelope?.meta?.total ?? quotations.length
  const totalPages = envelope?.meta?.totalPages ?? Math.ceil(totalCount / (filters?.limit || 10))

  Object.defineProperty(quotations, "meta", {
    value: envelope?.meta,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(quotations, "totalCount", {
    value: totalCount,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(quotations, "totalPages", {
    value: totalPages,
    enumerable: false,
    writable: true,
    configurable: true
  })

  return quotations
}

export async function getSellerQuotationById(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/quotation/seller-to-admin/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export interface ReviseQuotationPayload {
  message: string
  currencyCode: string
  originCityId?: string
  commodity?: string
  weight?: number
  volumeWeight?: number
  totalPieces?: number
  expiresAt?: string
  charges: {
    description: string
    unitPrice: number
    discount: number
    quantity: number | null
    measureUnit?: string
    tax: number
    taxType: string
  }[]
}

export interface ReviseAdminQuotationPayload {
  message: string
  currencyCode: string
  sellerToAdminQuotationId?: string
  expiresAt?: string
  charges: {
    description: string
    unitPrice: number
    discount: number
    quantity: number | null
    measureUnit?: string
    tax: number
    taxType: string
  }[]
}

export interface AddQuotationChargesPayload {
  charges: {
    description: string
    unitPrice: number
    discount: number
    quantity: number | null
    measureUnit?: string
    tax: number
    taxType: string
  }[]
}

export async function reviseSellerQuotation(
  id: string,
  payload: ReviseQuotationPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/quotation/seller-to-admin/${id}/revise`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function addQuotationCharges(
  id: string,
  payload: AddQuotationChargesPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/quotation/seller-to-admin/${id}/charges`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getAdminToBuyerQuotationById(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/quotation/admin-to-buyer/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function reviseAdminQuotation(
  id: string,
  payload: ReviseAdminQuotationPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/quotation/admin-to-buyer/${id}/revise`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function addAdminQuotationCharges(
  id: string,
  payload: AddQuotationChargesPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/quotation/admin-to-buyer/${id}/charges`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getAdminToBuyerQuotationCount(token: string): Promise<{ quotationCount: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ quotationCount: number }>>(
    "/quotation/admin-to-buyer/count",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getSellerToAdminQuotationCount(token: string): Promise<{ quotationCount: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ quotationCount: number }>>(
    "/quotation/seller-to-admin/count",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function requestRevisionAdminToBuyerQuotation(
  id: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/quotation/admin-to-buyer/${id}/request-revision`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function requestSellerRevisionAdminToBuyerQuotation(
  id: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/quotation/admin-to-buyer/${id}/request-seller-revision`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

// ── GET /quotation/buyer/response-activity-v2 ──────────────────────────────────
export interface ResponseActivityPoint {
  date: string
  timestamp?: string
  responsesReceived: number
  awaitingResponse: number
}

export interface ResponseActivityTimeframeSummary {
  totalResponsesReceived: number
  totalAwaitingResponse: number
  averageTurnaroundHours: number
  turnaroundText: string
}

export interface ResponseActivityTimeframeData {
  summary: ResponseActivityTimeframeSummary
  points: ResponseActivityPoint[]
}

export interface BuyerResponseActivityData {
  timeframes: Record<string, ResponseActivityTimeframeData>
}

export async function getBuyerResponseActivity(
  token: string,
  buyerId?: string
): Promise<BuyerResponseActivityData> {
  const { data: envelope } = await apiClient.get<Envelope<BuyerResponseActivityData>>(
    "/quotation/buyer/response-activity-v2",
    {
      params: buyerId ? { buyerId } : undefined,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}



